import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, switchMap, map } from 'rxjs';

import { DerivedAuthService } from '@shared/public-api';

import { environment } from '../../environments/environment';
import { SEVERITY_ORDER } from '../constants/holds.constants';
import {
  HoldDisplayLocationType,
  IFirestoreHoldConfig,
  IFirestoreHoldsApiResponse,
  IFirestoreHoldsResponse,
  IHoldApiItem,
  IHoldMessage,
} from '../types/holds.types';

export type {
  HoldSeverityType,
  HoldDisplayLocationType,
  IHoldMessage,
} from '../types/holds.types';
export { ROUTE_DISPLAY_MAP } from '../constants/holds.constants';

@Injectable({ providedIn: 'root' })
export class HoldsService {
  private readonly http = inject(HttpClient);
  private readonly derivedAuthService = inject(DerivedAuthService);

  /** Returns holds API headers with auth_type fallback as requested. */
  private get holdsApiHeaders(): HttpHeaders {
    const authType = sessionStorage.getItem('auth_type');
    const token = this.derivedAuthService.authToken();

    let headers = new HttpHeaders({
      AuthenticationType: authType || 'Student',
      'Ocp-Apim-Subscription-Key': `${environment.unifiedPortalGraphQlUrl.subscriptionKey}`,
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Adds the specified number of days to a date string and returns the result
   * formatted as `M/DD/YYYY`.
   * Used to compute the registration deadline shown alongside date-aware holds.
   * @param dateStr - ISO-formatted or parseable date string.
   * @param days - Number of days to add (may be negative).
   */
  private addDays(dateStr: string, days: number): string {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + days);
    const month = date.getMonth() + 1;
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Fetches hold configuration details from Firestore via HTTP API.
   * Returns hold title and body content keyed by hold code.
   */
  private getHoldsDetail(): Observable<IFirestoreHoldsResponse> {
    const params = new HttpParams()
      .set('collectionName', 'HOLD_VALUE')
      .set('documentName', 'HOLDS');

    return this.http
      .get<IFirestoreHoldsApiResponse>(environment.portalFirestoreUrl, {
        headers: {
          Authorization: `Bearer ${this.derivedAuthService.authToken()}`,
        },
        params,
      })
      .pipe(map(response => response.data));
  }

  /**
   * Maps raw hold API items and Firestore content into `IHoldMessage` objects.
   * Only hold codes present in both the API response and `HOLD_CONFIG` are included.
   * @param holdApiItems - Active holds returned from the holds API.
   * @param firestoreData - Hold display content from Firestore.
   */
  private mapHolds(
    holdApiItems: IHoldApiItem[],
    firestoreData: IFirestoreHoldsResponse,
  ): IHoldMessage[] {
    // Build a flat lookup map — normalize key to trim + uppercase for safe matching
    const configMap = new Map<string, IFirestoreHoldConfig>();
    for (const firestoreItem of firestoreData?.Items ?? []) {
      for (const config of firestoreItem.items ?? []) {
        const key = (config?.hashcode ?? config?.title ?? '')
          .trim()
          .toUpperCase();
        if (key) configMap.set(key, config);
      }
    }

    // Iterate API items so every active hold is processed
    return holdApiItems.reduce<IHoldMessage[]>((acc, apiItem) => {
      const config = configMap.get(apiItem.holdCode.trim().toUpperCase());
      if (config) {
        const baseMessage = config.studentMessage ?? '';
        const description =
          config.showDate && apiItem.holdStartDate
            ? `${baseMessage} ${this.addDays(apiItem.holdStartDate, 6)}`
            : baseMessage;

        acc.push({
          title: config.title ?? '',
          description,
          severity: config.severity ?? 'Low',
          resolution: config.resolution ?? '',
          displayLocations: config.displayLocations?.map(
            loc => loc.trim().toLowerCase() as HoldDisplayLocationType,
          ),
          holdStartDate: apiItem.holdStartDate,
          showButton: config.showButton ?? config.showbutton,
          buttonName: config.buttonName,
          url: config.url,
          showDate: config.showDate,
        });
      }
      return acc;
    }, []);
  }

  /**
   * Filters a list of holds to those visible at the given display location
   * and sorts the result by severity (High → Medium → Low).
   * @param holds - Full list of mapped hold messages.
   * @param displayLocation - The current route's display location.
   */
  filterByLocation(
    holds: IHoldMessage[],
    displayLocation: HoldDisplayLocationType,
  ): IHoldMessage[] {
    const normalizedLocation = displayLocation.trim().toLowerCase();
    return holds
      .filter(h =>
        h.displayLocations?.some(
          loc => loc.trim().toLowerCase() === normalizedLocation,
        ),
      )
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }

  /**
   * Fetches ALL active holds for the student with no location filtering.
   * Cache this result and use filterByLocation() for route-based display.
   */
  getAllHolds(bannerId: string): Observable<IHoldMessage[]> {
    const holdsCloudFunctionUrl = environment.holdsCloudFunctionUrl;

    if (!holdsCloudFunctionUrl) {
      return new Observable(observer => observer.next([]));
    }

    const holdsParams = new HttpParams().set(
      'uri',
      `${environment.holdsUrl}?studentId=${bannerId}`,
    );

    return this.http
      .get<IHoldApiItem[]>(holdsCloudFunctionUrl, {
        headers: this.holdsApiHeaders,
        params: holdsParams,
      })
      .pipe(
        switchMap(holdApiItems =>
          this.getHoldsDetail().pipe(
            map(firestoreData => this.mapHolds(holdApiItems, firestoreData)),
          ),
        ),
      );
  }

  /**
   * Fetches and returns active hold messages for the given banner ID,
   * filtered to the provided display location and sorted by severity (High → Medium → Low).
   */
  getHolds(
    bannerId: string,
    displayLocation: HoldDisplayLocationType,
  ): Observable<IHoldMessage[]> {
    const holdsCloudFunctionUrl = environment.holdsCloudFunctionUrl;

    if (!holdsCloudFunctionUrl) {
      return new Observable(observer => observer.next([]));
    }

    const holdsParams = new HttpParams().set(
      'uri',
      `${environment.holdsUrl}?studentId=${bannerId}`,
    );

    return this.http
      .get<IHoldApiItem[]>(holdsCloudFunctionUrl, {
        headers: this.holdsApiHeaders,
        params: holdsParams,
      })
      .pipe(
        switchMap(holdApiItems =>
          this.getHoldsDetail().pipe(
            map(firestoreData =>
              this.mapHolds(holdApiItems, firestoreData)
                .filter(h => h.displayLocations?.includes(displayLocation))
                .sort(
                  (a, b) =>
                    SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
                ),
            ),
          ),
        ),
      );
  }
}
